import { InputControl, TextareaControl } from "@shared/components/form";
import LinkFormControl from "@shared/components/form/LinkFormControl";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import React, { useId, useState } from "react";
import { toast } from "react-hot-toast";
import {
  RecordingItemData,
  RecordingsSectionData,
} from "../../types/home.types";

interface EditRecordingsSectionModalProps {
  initialData?: RecordingsSectionData;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<RecordingsSectionData>) => Promise<void>;
  isLoading?: boolean;
}

// Local interfaces for form state
interface FormValues extends Omit<RecordingsSectionData, "recordings"> {
  recordings: RecordingItemData[];
  viewAllTarget?: string;
}

interface FormErrors {
  title?: string;
  recordings?: string | Record<number, Record<string, string>>;
  viewAllText?: string;
  viewAllLink?: string;
  viewAllTarget?: string;
}

const EditRecordingsSectionModal: React.FC<EditRecordingsSectionModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  // Initialize form values from props or defaults
  const [formValues, setFormValues] = useState<FormValues>({
    title: initialData?.title || "",
    recordings: initialData?.recordings || [
      {
        month: "",
        description: "",
        buttonText: "Watch now",
        buttonLink: "",
        buttonTarget: "_self",
        imagePath: "",
      },
    ],
    viewAllText: initialData?.viewAllText || "View all recordings",
    viewAllLink: initialData?.viewAllLink || "",
    viewAllTarget: initialData?.viewAllTarget || "_self",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Media upload state for each recording
  const [mediaFiles, setMediaFiles] = useState<Record<number, MediaFile[]>>(
    Object.fromEntries(
      (initialData?.recordings || []).map((recording, index) => [
        index,
        recording.imagePath
          ? [
              {
                id: `recording-${index}`,
                file: new File([], `recording-${index}`),
                preview: recording.imagePath,
                uploadedUrl: recording.imagePath,
                type: MediaType.IMAGE,
                progress: 100,
              },
            ]
          : [],
      ])
    )
  );

  // Handle form field changes
  const handleChange = (field: keyof FormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle View All link changes
  const handleViewAllLinkChange = (field: string, value: string) => {
    const fieldMapping: Record<string, string> = {
      text: "viewAllText",
      link: "viewAllLink",
      target: "viewAllTarget",
    };

    handleChange(fieldMapping[field] as keyof FormValues, value);
  };

  // Handle recording field change
  const handleRecordingChange = (
    index: number,
    field: keyof RecordingItemData,
    value: string
  ) => {
    setFormValues((prev) => {
      const newRecordings = [...prev.recordings];
      newRecordings[index] = {
        ...newRecordings[index],
        [field]: value,
      };
      return {
        ...prev,
        recordings: newRecordings,
      };
    });

    // Clear validation errors for the field
    if (errors.recordings && Array.isArray(errors.recordings)) {
      setErrors((prev) => {
        if (!prev.recordings || !Array.isArray(prev.recordings)) {
          return prev;
        }

        const recordingErrors = [...prev.recordings];
        if (
          recordingErrors[index] &&
          typeof recordingErrors[index] === "object"
        ) {
          const fieldErrors = {
            ...(recordingErrors[index] as Record<string, string>),
          };
          delete fieldErrors[field];
          recordingErrors[index] = fieldErrors;
          return {
            ...prev,
            recordings: recordingErrors,
          };
        }
        return prev;
      });
    }
  };

  // Handle link field change for a recording
  const handleRecordingLinkChange = (
    index: number,
    field: "text" | "link" | "target",
    value: string
  ) => {
    const fieldMapping = {
      text: "buttonText",
      link: "buttonLink",
      target: "buttonTarget",
    };

    handleRecordingChange(
      index,
      fieldMapping[field] as keyof RecordingItemData,
      value
    );
  };

  // Handle media upload for a recording
  const handleMediaUpload = (index: number, files: MediaFile[]) => {
    setMediaFiles((prev) => ({
      ...prev,
      [index]: files,
    }));

    // Update recording imagePath
    if (files.length > 0) {
      handleRecordingChange(index, "imagePath", files[0].uploadedUrl || "");
    } else {
      handleRecordingChange(index, "imagePath", "");
    }

    // Clear validation errors for imagePath
    if (errors.recordings && Array.isArray(errors.recordings)) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (Array.isArray(newErrors.recordings)) {
          const recordingErrors = [...newErrors.recordings];
          if (
            recordingErrors[index] &&
            typeof recordingErrors[index] === "object"
          ) {
            const fieldErrors = {
              ...(recordingErrors[index] as Record<string, string>),
            };
            delete fieldErrors.imagePath;
            recordingErrors[index] = fieldErrors;
            newErrors.recordings = recordingErrors;
          }
        }
        return newErrors;
      });
    }
  };

  // Add a new recording
  const handleAddRecording = (position: "top" | "bottom") => {
    setFormValues((prev) => ({
      ...prev,
      recordings:
        position === "top"
          ? [
              {
                month: "",
                description: "",
                buttonText: "Watch now",
                buttonLink: "",
                buttonTarget: "_self",
                imagePath: "",
              },
              ...prev.recordings,
            ]
          : [
              ...prev.recordings,
              {
                month: "",
                description: "",
                buttonText: "Watch now",
                buttonLink: "",
                buttonTarget: "_self",
                imagePath: "",
              },
            ],
    }));

    // Initialize media files for the new recording
    setMediaFiles((prev) => ({
      ...prev,
      [formValues.recordings.length]: [],
    }));
  };

  // Remove a recording
  const handleRemoveRecording = (index: number) => {
    setFormValues((prev) => {
      const newRecordings = [...prev.recordings];
      newRecordings.splice(index, 1);
      return {
        ...prev,
        recordings: newRecordings,
      };
    });

    // Remove media files for the recording
    setMediaFiles((prev) => {
      const newMediaFiles = { ...prev };
      delete newMediaFiles[index];

      // Reindex remaining media files
      const reindexedMediaFiles: Record<number, MediaFile[]> = {};
      Object.keys(newMediaFiles).forEach((key, i) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          reindexedMediaFiles[numKey - 1] = newMediaFiles[numKey];
        } else {
          reindexedMediaFiles[numKey] = newMediaFiles[numKey];
        }
      });

      return reindexedMediaFiles;
    });

    // Clear errors for this recording if they exist
    if (errors.recordings && Array.isArray(errors.recordings)) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (Array.isArray(newErrors.recordings)) {
          const recordingErrors = [...newErrors.recordings];
          recordingErrors.splice(index, 1);
          newErrors.recordings = recordingErrors;
        }
        return newErrors;
      });
    }
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors: FormErrors = {};

    // Validate title
    if (!formValues.title.trim()) {
      newErrors.title = "Title is required";
    }

    // Validate viewAllText
    if (!formValues.viewAllText.trim()) {
      newErrors.viewAllText = "View All Text is required";
    }

    // Validate viewAllLink
    if (!formValues.viewAllLink.trim()) {
      newErrors.viewAllLink = "View All Link is required";
    }

    // Validate viewAllTarget
    if (!formValues.viewAllTarget) {
      newErrors.viewAllTarget = "View All Target is required";
    }

    // Validate recordings
    if (formValues.recordings.length === 0) {
      newErrors.recordings = "At least one recording is required";
    } else {
      const recordingErrors: Array<Record<string, string> | undefined> = [];
      let hasErrors = false;

      formValues.recordings.forEach((recording, index) => {
        const errors: Record<string, string> = {};

        if (!recording.month.trim()) {
          errors.month = "Month is required";
          hasErrors = true;
        }

        if (!recording.description.trim()) {
          errors.description = "Description is required";
          hasErrors = true;
        }

        if (!recording.buttonText.trim()) {
          errors.buttonText = "Button text is required";
          hasErrors = true;
        }

        if (!recording.buttonLink.trim()) {
          errors.buttonLink = "Button link is required";
          hasErrors = true;
        }

        if (!recording.buttonTarget) {
          errors.buttonTarget = "Button target is required";
          hasErrors = true;
        }

        recordingErrors[index] =
          Object.keys(errors).length > 0 ? errors : undefined;
      });

      if (hasErrors) {
        newErrors.recordings = recordingErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      await onSubmit(formValues);
      toast.success("Recordings section updated successfully");
    } catch (error) {
      console.error("Error updating recordings section:", error);
      toast.error("Failed to update recordings section");
    }
  };

  const modalId = useId();

  return (
    <ModalBlank
      id={modalId}
      modalOpen={isOpen}
      setModalOpen={() => onClose()}
      showCloseButton
    >
      <div className="p-5 sm:p-6">
        <div className="mb-5 text-center">
          <div className="mb-2">
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Edit Recordings Section
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Update the recordings section content
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="overflow-y-auto max-h-[calc(100vh-300px)] relative">
            {/* Title */}
            <div className="mb-4">
              <InputControl
                id="title"
                label="Title"
                value={formValues.title}
                onChange={(e) => handleChange("title", e.target.value)}
                error={errors.title}
                disabled={isLoading}
                required
              />
            </div>

            {/* Recordings */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recordings
              </label>

              <Button
                type="button"
                variant="secondary"
                onClick={() => handleAddRecording("top")}
                disabled={isLoading}
                fullWidth
                className="mb-2"
              >
                Add Recording
              </Button>

              {typeof errors.recordings === "string" && (
                <div className="text-sm text-red-500 mb-2">
                  {errors.recordings}
                </div>
              )}

              <div className="space-y-4 mb-4">
                {formValues.recordings.map((recording, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Recording {index + 1}</h4>
                      <Button
                        type="button"
                        variant="error"
                        size="sm"
                        onClick={() => handleRemoveRecording(index)}
                        disabled={
                          isLoading || formValues.recordings.length <= 1
                        }
                        fullWidth={false}
                      >
                        Remove
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <InputControl
                        id={`recording-${index}-month`}
                        label="Month"
                        value={recording.month}
                        onChange={(e) =>
                          handleRecordingChange(index, "month", e.target.value)
                        }
                        error={
                          errors.recordings &&
                          Array.isArray(errors.recordings) &&
                          errors.recordings[index] &&
                          (errors.recordings[index] as Record<string, string>)
                            .month
                        }
                        disabled={isLoading}
                        required
                      />

                      <TextareaControl
                        id={`recording-${index}-description`}
                        label="Description"
                        value={recording.description || ""}
                        onChange={(e) =>
                          handleRecordingChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        error={
                          errors.recordings &&
                          Array.isArray(errors.recordings) &&
                          errors.recordings[index] &&
                          (errors.recordings[index] as Record<string, string>)
                            .description
                        }
                        disabled={isLoading}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-1">
                        Button
                      </label>
                      <LinkFormControl
                        idPrefix={`recording-${index}-button`}
                        value={{
                          text: recording.buttonText,
                          link: recording.buttonLink,
                          target: recording.buttonTarget || "_self",
                        }}
                        onChange={(field, value) =>
                          handleRecordingLinkChange(index, field, value)
                        }
                        disabled={isLoading}
                        errors={{
                          text:
                            errors.recordings &&
                            Array.isArray(errors.recordings) &&
                            errors.recordings[index] &&
                            typeof errors.recordings[index] === "object"
                              ? (
                                  errors.recordings[index] as Record<
                                    string,
                                    string
                                  >
                                ).buttonText
                              : undefined,
                          link:
                            errors.recordings &&
                            Array.isArray(errors.recordings) &&
                            errors.recordings[index] &&
                            typeof errors.recordings[index] === "object"
                              ? (
                                  errors.recordings[index] as Record<
                                    string,
                                    string
                                  >
                                ).buttonLink
                              : undefined,
                          target:
                            errors.recordings &&
                            Array.isArray(errors.recordings) &&
                            errors.recordings[index] &&
                            typeof errors.recordings[index] === "object"
                              ? (
                                  errors.recordings[index] as Record<
                                    string,
                                    string
                                  >
                                ).buttonTarget
                              : undefined,
                        }}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Image
                      </label>
                      <MediaUploader
                        onUploadComplete={(files) =>
                          handleMediaUpload(index, files)
                        }
                        onError={(error) => console.error(error)}
                        maxFiles={1}
                        acceptType="image"
                      />
                      {errors.recordings &&
                        Array.isArray(errors.recordings) &&
                        errors.recordings[index] &&
                        (errors.recordings[index] as Record<string, string>)
                          .imagePath && (
                          <div className="text-sm text-red-500 mt-1">
                            {
                              (
                                errors.recordings[index] as Record<
                                  string,
                                  string
                                >
                              ).imagePath
                            }
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="secondary"
                onClick={() => handleAddRecording("bottom")}
                disabled={isLoading}
                fullWidth
              >
                Add Recording
              </Button>
            </div>

            {/* View All Link */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">
                View All Button
              </label>
              <LinkFormControl
                idPrefix="view-all-button"
                value={{
                  text: formValues.viewAllText,
                  link: formValues.viewAllLink,
                  target: formValues.viewAllTarget || "_self",
                }}
                onChange={handleViewAllLinkChange}
                disabled={isLoading}
                errors={{
                  text: errors.viewAllText,
                  link: errors.viewAllLink,
                  target: errors.viewAllTarget,
                }}
                required
              />
            </div>
          </div>

          {/* Form actions - fixed at bottom */}
          <div className="bg-white dark:bg-gray-800 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={isLoading} loadingText="Saving...">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </ModalBlank>
  );
};

export default EditRecordingsSectionModal;
